import { Component, OnDestroy, OnInit, WritableSignal, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { debounceTime, filter, Observable, Subject, take, takeUntil, tap } from 'rxjs';
import { IEmployeeListItem } from 'src/app/shared/interfaces/IEmployeeListItem';
import { IEmployeePickerDialogData } from 'src/app/shared/interfaces/IEmployeePickerDialogData';
import { IEmployeeSCDItem } from 'src/app/shared/interfaces/IEmployeeSCDItem';
import { IFilterOptions } from 'src/app/shared/interfaces/IFilterOptions';
import { Store } from '@ngxs/store';
import { UserState } from 'src/app/shared/states/user.state';
import { EmployeeOperations } from 'src/app/shared/operations/employee.operations';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TableColumn, TableFilterState, TableComponent } from '../../table/table.component';
import { employeePickerFilterFields, getEmployeePickerConfig } from '../../../configs/employee-picker.config';
import { IPersistentFilter } from '../../../interfaces/employee/IPersistentFilter';
import { IPersistentFilterColumn } from '../../../interfaces/IPersistentFilterColumnSetting';
import { UpdatePersistentFilters } from '../../../states/user.actions';
import { IFilterField } from '../../../interfaces/IFilterField';
import { ActionsHeaderComponent } from '../../actions-header/actions-header.component';
import { IPaginatedResponse, LastRead, PageChangedEvent, SortedLastReadValue } from '../../../interfaces/IPaginatedResponse';
import { EmployeeState } from '../../../states/employee.state';
import { TABLE_MODE } from '../../../enums/TableMode.enum';
import { IFilter } from '../../../interfaces/IFilter';
import { LoadEmployees } from '../../../states/employee.actions';
import { PaginationOperations } from '../../../operations/pagination.operations';
import { EmployeePickerOperations } from './operations/employee-picker.operations';

@Component({
  selector: 'app-employee-picker-dialog',
  templateUrl: './employee-picker-dialog.component.html',
  styleUrls: ['./employee-picker-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    ActionsHeaderComponent,
    TableComponent,
    MatDialogActions,
    TranslatePipe,
    AsyncPipe,
  ],
})
export class EmployeePickerDialogComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<EmployeePickerDialogComponent>>(MatDialogRef);
  private store = inject(Store);
  private translateService = inject(TranslateService);
  employeePickerData = inject<IEmployeePickerDialogData>(MAT_DIALOG_DATA);
  private employees$: Observable<IPaginatedResponse<IEmployeeListItem>> = this.store.select<IPaginatedResponse<IEmployeeListItem>>(
    EmployeeState.employees,
  );
  private userFilters$: Observable<IPersistentFilter> = this.store.select<IPersistentFilter>(UserState.filters);

  protected employeePickerIsLoading$: Observable<boolean> = this.store.select<boolean>(EmployeeState.isEmployeePickerLoading);
  protected newlySelectedEmployees: IEmployeeListItem[] = [];
  protected searchValue = '';
  protected employeePickerColumnConfig: TableColumn<IEmployeeListItem>[];
  protected preSelectedPersistentFilters: TableFilterState;
  protected searchFilterFields: IFilterField[] = employeePickerFilterFields;
  protected employees: IEmployeeListItem[] = [];
  protected filterOptions: IFilterOptions;
  private userPersistentFilters: IPersistentFilter;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private filterSubject = new Subject<IFilterOptions>();

  protected readonly TABLE_MODE = TABLE_MODE;

  // All relevant signals used for the pagination on the server
  protected lastRead: WritableSignal<LastRead> = signal({ currentPageLastRead: 0, nextPageLastRead: 0 });
  protected sortedLastReadValue: WritableSignal<SortedLastReadValue> = signal({
    currentSortedLastReadValue: null,
    nextSortedLastReadValue: null,
  });
  protected totalCount: WritableSignal<number> = signal(0);
  protected currentPage: WritableSignal<number> = signal(1);
  protected pageSize: WritableSignal<number> = signal(30);
  protected sort: WritableSignal<string> = signal('scdName, ASC');
  protected appliedFilters: WritableSignal<IFilter[]> = signal([]);

  private pageLastReadMap = new Map<number, LastRead>();
  private sortLastReadMap = new Map<number, SortedLastReadValue>();

  protected get getFilterValuesEndpoint(): string {
    return `Employees/GetFilterValuesByStatus`;
  }

  ngOnInit(): void {
    this.loadTableConfigInformation();
    this.loadEmployees();
    this.setEmployeeColumns();
    this.subscribeToSearchValueChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  protected onSearchValueChange(event: IFilterOptions): void {
    this.filterSubject.next(event);
  }

  protected clearPersistentFilters(): void {
    if (this.preSelectedPersistentFilters) {
      this.dispatchUpdatedPersistentFilter(null);
    }
    this.preSelectedPersistentFilters = null;
  }

  protected addOrRemoveEmployeeToSelection(selectedEmployees: IEmployeeListItem[]): void {
    if (selectedEmployees && selectedEmployees.length) {
      this.newlySelectedEmployees = selectedEmployees;
    }
  }

  protected emitSelectedEmployees(): void {
    const selectedItems: IEmployeeSCDItem[] = (this.employeePickerData.alreadySelectedEmployees ?? []).concat(
      (this.newlySelectedEmployees ?? []).map(employee => this.mapEmployeeListItemToEmployeeSCDItem(employee)),
    );

    const distinctItems = selectedItems.filter(
      (item, index, self) => index === self.findIndex(employee => employee.scdGid === item.scdGid),
    );

    this.dialogRef.close(distinctItems);
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected updatePersistentFilter(tableFilterState: TableFilterState): void {
    const updatedPreSelectedPersistentFilters = EmployeePickerOperations.getUpdatedPreSelectedPersistentFilters(tableFilterState);
    this.dispatchUpdatedPersistentFilter(updatedPreSelectedPersistentFilters);
  }

  /**
   * Handles the page change event.
   * Get the new last read and sorted last read value based on the page change event, the last read signal, and the sorted last read value signal,
   * also considering the pageLastReadMap and sortLastReadMap.
   * It also updates the currentPage and the pageSize
   * Dispatches the load employees action without resetting the pagination.
   *
   * @param {PageChangedEvent} pageChangedEvent - The event containing the details of the page change.
   */
  protected onPageChanged(pageChangedEvent: PageChangedEvent): void {
    const { newLastRead, newSortedLastReadValue } = PaginationOperations.onPageChanged(
      this.pageSize,
      this.currentPage,
      this.lastRead,
      this.sortedLastReadValue,
      pageChangedEvent,
      this.pageLastReadMap,
      this.sortLastReadMap,
    );
    this.dispatchLoadEmployees(false, newLastRead, newSortedLastReadValue);
  }

  /**
   * Handles the server-side sort change event.
   * Updates the sort order and resets the sorted last read value, because the pagination is reset when the sort changes
   * Dispatches the load employees action with pagination reset.
   *
   * @param {string} sort - The new sort order.
   */
  protected onServerSortChange(sort: string): void {
    this.sort.set(sort);
    this.sortedLastReadValue.set({ currentSortedLastReadValue: null, nextSortedLastReadValue: null });
    this.dispatchLoadEmployees(true);
  }

  /**
   * Handles the filter change event.
   * Updates the applied filters and dispatches the load employees action with pagination reset.
   * We filter for filters that have values, because we don't want to apply filters that have no values, as this crashes the backend.
   *
   * @param {IFilter[]} filters - The new filters to be applied.
   */
  protected onFilterChange(filters: IFilter[]): void {
    const tableFilterState = EmployeePickerOperations.transformFiltersToTableFilterState(filters);
    this.updatePersistentFilter(tableFilterState);

    const validFilters = filters.filter(filter => filter.values.length > 0);
    // Restore preconfigured filters if they were removed or emptied so that the user cannot circumvent mandatory filters
    const restoredFilters = this.ensurePreconfiguredFiltersArePresent(validFilters, filters);

    this.appliedFilters.set(restoredFilters);
    this.dispatchLoadEmployees(true);
  }

  /**
   * Clears all applied filters and resets the sort order.
   * Sets the applied filters to an empty array and the sort order to null.
   * Doesn't dispatch the load employees action, because this happens in subscribeToSearchValueChanges, since the filterOptions change in the app-actions-header component.
   */
  protected clearFilters(): void {
    this.appliedFilters.set([]);
    this.clearPersistentFilters();
    this.applyPreconfiguredFilters();
    this.sort.set(null);
  }

  /**
   * Subscribes to search value changes and dispatches the load employees action with a debounce time.
   * The filter options are updated and the load employees action is dispatched after the debounce time.
   * The handling of the filterOptions is done in the dispatchLoademployees method.
   * The subscription is automatically unsubscribed when the component is destroyed.
   */
  private subscribeToSearchValueChanges(): void {
    this.filterSubject
      .pipe(
        debounceTime(750),
        tap(option => (this.filterOptions = option)),
        tap(() => this.dispatchLoadEmployees(true)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private dispatchUpdatedPersistentFilter(updatedPreSelectedPersistentFilters: IPersistentFilterColumn): void {
    const persistentFilterBody = EmployeePickerOperations.getPersistentFilterBody(
      updatedPreSelectedPersistentFilters,
      this.userPersistentFilters.scdGid,
    );
    this.store.dispatch(new UpdatePersistentFilters(persistentFilterBody, 'controllingFilter'));
  }

  private loadEmployees(): void {
    this.employees$
      .pipe(
        filter(employeesPaginationResponse => !!employeesPaginationResponse),
        tap(employeesPaginationResponse => {
          this.initializeEmployeesAndApplyFilters(employeesPaginationResponse);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private loadTableConfigInformation(): void {
    this.userFilters$
      .pipe(
        take(1), // Only take the first emission to avoid duplicate calls on filter updates
        tap(userFilters => this.initializePersistentFiltersAndTableConfig(userFilters)),
        tap(() => {
          this.dispatchLoadEmployees(true);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private mapEmployeeListItemToEmployeeSCDItem(employeeItem: IEmployeeListItem): IEmployeeSCDItem {
    return {
      scdName: employeeItem.scdName,
      scdEMail: employeeItem.scdEMail,
      scdGid: employeeItem.scdGid,
      location: employeeItem.location,
      lastMonthlySettlement: employeeItem.lastMonthlySettlement,
    };
  }

  private initializePersistentFiltersAndTableConfig(userPersistentFilters: IPersistentFilter): void {
    this.userPersistentFilters = userPersistentFilters;
    this.preSelectedPersistentFilters = EmployeePickerOperations.initializePersistentFilters(this.userPersistentFilters);

    const preconfiguredFilterKeys = this.employeePickerData.filter?.map(filter => filter.key) || [];
    const persistentFilters = EmployeePickerOperations.extractFiltersFromTableFilterState(this.preSelectedPersistentFilters);
    const nonConflictingPersistentFilters = persistentFilters.filter(filter => !preconfiguredFilterKeys.includes(filter.key));

    this.appliedFilters.set(this.appliedFilters().concat(nonConflictingPersistentFilters));
    this.applyPreconfiguredFilters();
  }

  private applyPreconfiguredFilters(): void {
    if (this.employeePickerData.filter) {
      const currentFilters = this.appliedFilters();
      const updatedFilters = [...currentFilters];

      this.employeePickerData.filter.forEach(preconfiguredFilter => {
        const existingFilterIndex = updatedFilters.findIndex(filter => filter.key === preconfiguredFilter.key);

        if (existingFilterIndex !== -1) {
          const existingValues = updatedFilters[existingFilterIndex].values;
          const mergedValues = [...new Set([...existingValues, ...preconfiguredFilter.values])];
          updatedFilters[existingFilterIndex] = {
            ...updatedFilters[existingFilterIndex],
            values: mergedValues,
          };
        } else {
          updatedFilters.push({
            ...preconfiguredFilter,
            values: [...preconfiguredFilter.values],
          });
        }
      });

      this.appliedFilters.set(updatedFilters);
    }
  }

  private ensurePreconfiguredFiltersArePresent(validFilters: IFilter[], _originalFilters: IFilter[]): IFilter[] {
    const restoredFilters = [...validFilters];

    // Check each preconfigured filter and restore it if missing from validFilters
    this.employeePickerData.filter?.forEach(preconfiguredFilter => {
      const existingFilterIndex = restoredFilters.findIndex(filter => filter.key === preconfiguredFilter.key);

      if (existingFilterIndex === -1) {
        restoredFilters.push({
          ...preconfiguredFilter,
          values: [...preconfiguredFilter.values],
        });
      } else if (restoredFilters[existingFilterIndex].values.length === 0) {
        restoredFilters[existingFilterIndex] = {
          ...restoredFilters[existingFilterIndex],
          values: [...preconfiguredFilter.values],
        };
      }
    });

    return restoredFilters;
  }

  /**
   * Initializes the employees and applies filters based on the paginated response.
   * IMPORTANT: Updates the last read signal, sorted last read value, total count, and employee items.
   * Otherwise, the pagination will not work correctly
   * Also updates the columns based on the new employee data.
   *
   * @param {IPaginatedResponse<IEmployeeListItem>} employeesPaginationResponse - The paginated response containing employee data.
   */
  private initializeEmployeesAndApplyFilters(employeesPaginationResponse: IPaginatedResponse<IEmployeeListItem>): void {
    PaginationOperations.updateLastReadSignal(
      this.lastRead,
      employeesPaginationResponse.lastRead,
      this.currentPage(),
      this.pageLastReadMap,
    );
    if (employeesPaginationResponse?.sortedLastReadValue || employeesPaginationResponse?.sortedLastReadValue === '') {
      PaginationOperations.updateSortedLastReadValue(
        this.sortedLastReadValue,
        employeesPaginationResponse?.sortedLastReadValue,
        this.currentPage(),
        this.sortLastReadMap,
      );
    }
    this.totalCount.set(employeesPaginationResponse.totalCount);
    this.employees = employeesPaginationResponse.items;
    this.setEmployeeColumns();
  }

  /**
   * Dispatches the load employees action with the provided pagination and filter options.
   * If resetPagination is true, it resets the pagination before dispatching the action.
   * Every value that needs to be applied to the pagination is passed as an argument.
   * Based on those values we build the pagination props and dispatch the load employees action.
   *
   * @param {boolean} resetPagination - Indicates whether to reset the pagination.
   * @param {number} [newLastRead=0] - The new last read value for pagination.
   * @param {string} [newSortedLastReadValue=null] - The new sorted last read value for pagination.
   */
  private dispatchLoadEmployees(resetPagination: boolean, newLastRead = 0, newSortedLastReadValue: string = null): void {
    if (resetPagination) {
      PaginationOperations.resetPagination(this.lastRead, this.currentPage, this.pageLastReadMap, this.sortLastReadMap);
    }

    this.store.dispatch(
      new LoadEmployees(
        EmployeeOperations.buildEmployeePaginationProps(
          this.pageSize(),
          this.appliedFilters(),
          newLastRead,
          this.sort(),
          newSortedLastReadValue,
          this.filterOptions ? this.filterOptions.searchValue : null,
          this.filterOptions ? this.filterOptions.compareOperator : null,
          this.filterOptions
            ? this.filterOptions.filterFields.filter(filterFields => filterFields.isSelected).map(filterFields => filterFields.key)
            : null,
          null,
          true,
        ),
      ),
    );
  }

  private setEmployeeColumns(): void {
    this.employeePickerColumnConfig = getEmployeePickerConfig(this.translateService);
  }
}
